export type HelpSegmentKind = 'text' | 'strong' | 'code';

export interface HelpSegment {
  readonly kind: HelpSegmentKind;
  readonly value: string;
}

export type HelpBlock =
  | { readonly kind: 'paragraph'; readonly segments: readonly HelpSegment[] }
  | { readonly kind: 'list'; readonly items: readonly (readonly HelpSegment[])[] }
  | { readonly kind: 'code'; readonly value: string };

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

/**
 * Parses the Markdown subset help texts are allowed to use: paragraphs, `- ` lists, `**strong**`,
 * `` `code` `` and **fenced code blocks**.
 *
 * Returns a structure rather than a string **because the result is never assigned to innerHTML**.
 * Help texts come from translation resources, and rendering those as HTML would be a maintenance
 * and injection problem. The template renders the blocks with ordinary Angular bindings, so nothing
 * here can inject markup.
 *
 * **The fenced block was added on 2026-08-26 for the service hooks**, and it is the one thing inline
 * code could not stand in for: a request body is several lines whose line breaks carry meaning, and
 * a paragraph collapses them into one run. `UI.Angular.Core` shows the same contract in a `<pre>`;
 * the difference is that here it sits behind the help icon instead of taking up a third of the form.
 *
 * Links are deliberately not supported yet -- when they are, they will need target and scheme
 * validation, and that decision has not been taken.
 */
export function parseHelpText(text: string): readonly HelpBlock[] {
  const blocks: HelpBlock[] = [];

  /*
   * Fences first, because everything else splits on blank lines and would cut a code block apart.
   * Odd indices are what sat between a pair of fences; an unmatched fence therefore leaves its tail
   * as ordinary prose, which is a visible mistake rather than a silent one.
   */
  text.split('```').forEach((part, index) => {
    if (index % 2 === 1) {
      const value = part.replace(/^\n/, '').replace(/\n$/, '');

      if (value.trim() !== '') {
        blocks.push({ kind: 'code', value });
      }

      return;
    }

    blocks.push(...parseProse(part));
  });

  return blocks;
}

function parseProse(text: string): readonly HelpBlock[] {
  return text
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(block => block.length > 0)
    .map(block => (isList(block) ? parseList(block) : parseParagraph(block)));
}

function isList(block: string): boolean {
  return block.split('\n').every(line => line.trimStart().startsWith('- '));
}

function parseList(block: string): HelpBlock {
  const items = block
    .split('\n')
    .map(line => line.trimStart().slice(2).trim())
    .filter(line => line.length > 0)
    .map(parseInline);

  return { kind: 'list', items };
}

function parseParagraph(block: string): HelpBlock {
  return { kind: 'paragraph', segments: parseInline(block.replace(/\n/g, ' ')) };
}

function parseInline(text: string): readonly HelpSegment[] {
  return text
    .split(INLINE)
    .filter(part => part.length > 0)
    .map(part => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return { kind: 'strong' as const, value: part.slice(2, -2) };
      }

      if (part.startsWith('`') && part.endsWith('`')) {
        return { kind: 'code' as const, value: part.slice(1, -1) };
      }

      return { kind: 'text' as const, value: part };
    });
}